import {
  isMultiProductOrderTreatmentComplete,
  pendingMultiProductOrderFamilyTreatments
} from '@/lib/api/med-requests/multi-product-order/family-actions'
import {
  decidableMultiProductOrderTreatmentIds,
  hasDecisionForEveryPendingTreatment,
  hasMultiProductOrderFollowUpTarget,
  isOpenedTreatmentPending
} from '@/lib/med-requests'
import useSubmitTreatmentDecisions from '@/lib/api/med-requests/multi-product-order/treatment-decision'
import { canSendMessages } from '@/lib/permissions'
import { useMedicationRequestStore } from '@/lib/stores/medication-request'
import { cn, isFutureIssueDate } from '@/lib/utils'
import { FooterActions, useFooterActionsState } from '../footer-actions'

/**
 * "Approve all" / "Follow Up" / "Reject all" decide every pending treatment (Follow Up, with none
 * left, the decidable ones); "Submit decision" sends the decisions recorded on the cards once every
 * pending treatment has one.
 */
export default function MultiProductOrderFooter() {
  const {
    medicationIssueDate,
    medicationDirection,
    medication,
    multiProductOrderTreatments,
    setDecisionPanelTarget,
    treatmentDecisions,
    isTreatmentDecisionSubmitting
  } = useMedicationRequestStore()
  const state = useFooterActionsState()
  const { data } = state

  const { submit: submitTreatmentDecisions } = useSubmitTreatmentDecisions()

  if (!data) return null

  const isOpenedPending = isOpenedTreatmentPending(data)
  const pendingFamilyTreatments = pendingMultiProductOrderFamilyTreatments(data, multiProductOrderTreatments)
  const hasIncompleteFamilyTreatment = pendingFamilyTreatments.some(
    treatment => !isMultiProductOrderTreatmentComplete(treatment)
  )
  const pendingMedications = [
    ...(isOpenedPending ? [medication] : []),
    ...pendingFamilyTreatments.map(treatment => treatment?.medication ?? null)
  ]
  const hasPendingTreatment = pendingMedications.length > 0

  // All-OTC skips the approval reason, but unlike a lone OTC request still needs the scroll.
  const isOtcOnly = hasPendingTreatment && pendingMedications.every(entry => entry?.type === 'OTC')

  const recordedDecisions = decidableMultiProductOrderTreatmentIds(data).flatMap(id => treatmentDecisions[id] ?? [])
  // While cards hold recorded decisions, the footer's own actions stay off so they don't override them.
  const hasRecordedDecisions = recordedDecisions.length > 0
  const hasRecordedApproval = recordedDecisions.some(decision => decision.action === 'approve')

  // Any approval, from the footer or a card, waits for the scroll unless the request is a priority.
  const isReviewPending = !state.isScrolledDown && !data.isPriority

  const isApprovalDisabled =
    state.isDecisionBlocked ||
    hasRecordedDecisions ||
    !hasPendingTreatment ||
    (isOpenedPending && (!medication || !medicationDirection.trim())) ||
    isReviewPending ||
    hasIncompleteFamilyTreatment

  // With nothing to decide they only open an existing conversation; read-only roles keep them open.
  const canSend = canSendMessages()

  const isFollowUpDisabled =
    state.isFollowUpOrRejectBlocked ||
    (canSend && (hasRecordedDecisions || (!hasMultiProductOrderFollowUpTarget(data) && !state.isFollowupBadgeVisible)))

  const isRejectDisabled =
    state.isFollowUpOrRejectBlocked ||
    (canSend && (hasRecordedDecisions || (!hasPendingTreatment && !state.isRejectionBadgeVisible)))

  const isSubmitDecisionDisabled =
    // With every item already decided nothing is pending, so at least one card decision is still required.
    !hasRecordedDecisions ||
    !hasDecisionForEveryPendingTreatment(data, treatmentDecisions) ||
    !canSend ||
    state.isDecisionBlocked ||
    (hasRecordedApproval && isReviewPending)

  return (
    <FooterActions
      state={state}
      leadingAction={
        <button
          type='button'
          data-testid='submit-decision'
          disabled={isSubmitDecisionDisabled}
          onClick={() => {
            // Closes the panel; only recorded decisions are sent.
            setDecisionPanelTarget(null)
            submitTreatmentDecisions()
          }}
          className={cn(
            'flex w-[200px] h-14 justify-center items-center rounded-2xl bg-[#153CB7] py-4 px-6 hover:bg-[#0F2E8A] transition-colors duration-200',
            {
              'opacity-50 cursor-not-allowed': isSubmitDecisionDisabled,
              'cursor-wait': isTreatmentDecisionSubmitting
            }
          )}
        >
          <span className='text-white text-center text-sm font-semibold leading-[170%]'>
            {isTreatmentDecisionSubmitting ? 'Submitting…' : 'Submit decision'}
          </span>
        </button>
      }
      approveLabel={isFutureIssueDate(medicationIssueDate) ? 'Approve all for future' : 'Approve all'}
      isApprovalDisabled={isApprovalDisabled}
      skipsApprovalReason={isOtcOnly}
      rejectLabel='Reject all'
      isFollowUpDisabled={isFollowUpDisabled}
      isRejectDisabled={isRejectDisabled}
      // Footer actions decide every treatment, so the card's panel closes.
      onAction={() => setDecisionPanelTarget(null)}
    />
  )
}
